/**
 *
 * Transport Details Page
 *
 */
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Descriptions,
  Dropdown,
  Menu,
  PageHeader,
  Tag,
  Modal,
  notification,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, RouteComponentProps } from 'react-router';

import { RouteConstants } from 'app/routes';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { useArchiveTransportMutation, useGetTransportByIdQuery } from 'app/api';
import { MainContainer } from 'app/components/LayoutComponents/LayoutComponents';
import { EllipsisIcon } from 'app/components/CommonIcons/CommonIcons';
import { useGlobalSlice } from 'app/containers/Global/slice';
import TransportDrawer from '../../parts/TransportDrawer/TransportDrawer';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { PermissionsEnum } from 'app/containers/User/slice/types';

interface IReactRouterParams {
  transportId: string;
  companyId: string;
}

interface IProps extends RouteComponentProps<IReactRouterParams> {}

const TransportDetailsPage: FC<IProps> = ({ history, match: { params } }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const globalActions = useGlobalSlice().actions;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [
    archiveTransport,
    { isLoading: isArchiveLoading },
  ] = useArchiveTransportMutation();

  const envId = useSelector(selectSelectedEnvId);

  const transportId = Number(params.transportId);
  const companyId = Number(params.companyId);

  const { data: transport, isFetching } = useGetTransportByIdQuery(
    { envId: envId as number, transportId, companyId },
    { skip: !envId || !transportId || !companyId },
  );

  const onArchive = () => {
    Modal.confirm({
      title: t(
        'Transport.TransportArchiveConfirm',
        'Are you sure you want to archive this transport?',
      ),
      onOk: onArchiveConfirm,
      okText: t('Common.Yes', 'Yes'),
      cancelText: t('Common.No', 'No'),
    });
  };

  const goBackToList = () => {
    history.push(
      generatePath(RouteConstants.garage.transportList, {
        envId: envId as number,
      }),
    );
  };

  const onArchiveConfirm = async () => {
    try {
      await archiveTransport({
        envId: envId as number,
        companyId,
        transportId,
      }).unwrap();

      goBackToList();

      notification.success({
        message: t('Transport.TransportArchivedSuccessfully', {
          defaultValue: 'Transport "{{transportModel}}" archived successfully',
          transportModel: transport?.model,
        }),
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch(globalActions.handleError(error));
    }
  };

  return (
    <>
      <PageHeader
        onBack={goBackToList}
        className="page-header"
        title={transport?.model}
      />
      <MainContainer>
        <Card
          style={{ width: '100%', minHeight: 'calc(100vh - 190px)' }}
          title={t('Transport.Information', 'Information')}
          loading={isFetching || isArchiveLoading}
          extra={[
            <PermissionsWrapper
              requiredEditPermissions={[PermissionsEnum.garageAdministration]}
              key="add"
            >
              <Dropdown
                trigger={['click']}
                overlay={
                  <Menu>
                    <Menu.Item onClick={() => setIsDrawerOpen(true)}>
                      {t('Transport.ChangeInformation', 'Change information')}
                    </Menu.Item>

                    <Menu.Item onClick={onArchive}>
                      {t('Common.Archive', 'Archive')}
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button type="primary" shape="circle">
                  <EllipsisIcon size={14} />
                </Button>
              </Dropdown>
            </PermissionsWrapper>,
          ]}
        >
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('Transport.Model', 'Model')}>
              {transport?.model ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('Transport.LicensePlate', 'License plate')}
            >
              {transport?.licensePlate ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('Transport.Year', 'Year of production')}
            >
              {transport?.year ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item label={t('Transport.Type', 'Type')}>
              {transport?.type.name ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item label={t('Transport.MotoHours', 'Motohours')}>
              {transport?.motoHours ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item
              label={t('Transport.Width', 'Operating width (m)')}
            >
              {transport?.width ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item label={t('Transport.GpsId', 'GPS ID')}>
              {transport?.gpsVehicle?.gpsId ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item
              span={2}
              label={t('Transport.Ownership', 'Ownership')}
            >
              {transport?.company.name}
            </Descriptions.Item>

            <Descriptions.Item
              span={2}
              label={t(
                'Transport.MainCategoryWithouImplement',
                'Main category of tasks to be performed without the implement',
              )}
            >
              {transport?.defaultTaskCategory?.name ? (
                <Tag>{transport.defaultTaskCategory.name}</Tag>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item
              span={2}
              label={t(
                'Transport.CategoriesWithoutImplement',
                'Categories of tasks to be performed without the implement',
              )}
            >
              {transport?.taskCategories.length
                ? transport?.taskCategories?.map(taskCategory => (
                    <Tag key={taskCategory.id}>{taskCategory.name}</Tag>
                  ))
                : '-'}
            </Descriptions.Item>

            {transport?.accountingMethods.map(accountingMethod => (
              <React.Fragment key={accountingMethod.id}>
                <Descriptions.Item
                  span={2}
                  label={t('Transport.CalculationMethod', 'Calculation method')}
                >
                  <Tag>{accountingMethod.name}</Tag>
                </Descriptions.Item>
                <Descriptions.Item
                  span={2}
                  label={t('Transport.CostPrice', 'Cost price')}
                >
                  {accountingMethod.costPrice ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item
                  span={2}
                  label={t('Transport.SellPrice', 'Sell price')}
                >
                  {accountingMethod.sellPrice ?? '-'}
                </Descriptions.Item>
              </React.Fragment>
            ))}
          </Descriptions>
        </Card>
      </MainContainer>
      <TransportDrawer
        transport={transport}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </>
  );
};

export default TransportDetailsPage;
